import type { ScheduleDashboardSchedule } from '../SchedulesDashboard/types';

type DashboardResponseData = {
  schedulesById?: Record<string, ScheduleDashboardSchedule>;
};

/**
 * `camelize` is intentionally used for Elasticsearch aggregation field names, but it must not
 * process dictionaries keyed by identifiers: a key such as `schedule-1` becomes `schedule1`.
 */
export const getDashboardSchedulesById = (
  result: unknown,
): Record<string, ScheduleDashboardSchedule> => {
  if (typeof result !== 'object' || result === null || !('data' in result)) return {};

  const data = (result as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('schedulesById' in data)) return {};

  const schedulesById = (data as DashboardResponseData).schedulesById;
  if (!schedulesById || Array.isArray(schedulesById)) return {};

  return schedulesById;
};

export const getDashboardScheduleDivisionIds = (
  schedulesById: Record<string, ScheduleDashboardSchedule>,
): string[] =>
  Array.from(
    new Set(
      Object.values(schedulesById)
        .map(schedule => schedule.divId || schedule.divisionId)
        .filter((divisionId): divisionId is string => Boolean(divisionId)),
    ),
  );

export const getDashboardSchedulesWithDivisionNames = (
  schedulesById: Record<string, ScheduleDashboardSchedule>,
  getDivisionName: (divisionId: string) => string,
): Record<string, ScheduleDashboardSchedule> =>
  Object.fromEntries(
    Object.entries(schedulesById).map(([scheduleId, schedule]) => {
      const divisionId = schedule.divId || schedule.divisionId;
      return [
        scheduleId,
        divisionId ? { ...schedule, divisionName: getDivisionName(divisionId) } : schedule,
      ];
    }),
  );
